import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from "@angular/common/http";
import { Inject, Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { AnonymousAuthService, AuthenticationService } from "@vp/shared/authentication";
import {
  ANON_CONFIG,
  APIM_SUB_KEY,
  APIM_TRACE,
  BYPASS_AUTH_INTERCEPTOR,
  OKTA_ROUTES
} from "@vp/shared/tokens";
import { Observable } from "rxjs";
import { concatMap, mergeMap, take } from "rxjs/operators";

@Injectable({
  providedIn: "root"
})
export class AuthenticationHeaderInterceptor implements HttpInterceptor {
  constructor(
    @Inject(OKTA_ROUTES) private oktaApiRoutes: string[],
    @Inject(ANON_CONFIG)
    private readonly anonConfig: { url: string; origin: string; apiSuffix: string },
    @Inject(APIM_TRACE) private readonly apimTrace: string,
    @Inject(APIM_SUB_KEY) private readonly apimSubscriptionKey: string,
    private authenticationService: AuthenticationService,
    private anonAuthService: AnonymousAuthService,
    private router: Router
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // skip auth interceptor
    if (request.context.get(BYPASS_AUTH_INTERCEPTOR)) {
      return next.handle(request);
    }

    const url = new URL(request.url);

    //only add APIM and Token headers if its our API
    if (!this.oktaApiRoutes.find(x => url.origin.includes(x))) {
      return next.handle(request);
    }

    // handle anonymous
    if (this.anonConfig) {
      let apiPath = request.url;

      const rootPath = url.pathname.split("/")[1];
      apiPath = apiPath.replace(rootPath, this.anonConfig.apiSuffix);

      return this.anonAuthService.getAnonAccessToken().pipe(
        concatMap((accessToken: string) => {
          const anonRequest = request.clone({
            url: apiPath,
            headers: request.headers
              .set("Ocp-Apim-Subscription-Key", this.apimSubscriptionKey)
              .set("Ocp-Apim-Trace", this.apimTrace)
              .set("Authorization", "Bearer " + accessToken)
              .set("x-ui-local-offset", new Date().getTimezoneOffset().toString())
          });

          return next.handle(anonRequest);
        })
      );
    }

    this.authenticationService.isLoggedIn$().subscribe(authenticatedResult => {
      if (!authenticatedResult.isAuthenticated && !this.anonConfig) {
        if (
          request.urlWithParams.includes("callback") ||
          request.urlWithParams.includes("silent-renew")
        ) {
          this.authenticationService.login();
        }

        if (!this.authenticationService.isLoggingOut) {
          this.router.navigate(["/login"]);
        }
      }
    });

    return this.authenticationService.getAccessToken().pipe(
      take(1),
      mergeMap((token: string | null) => {
        if (token) {
          // Clone the request to add the new header
          const clonedRequest = request.clone({
            headers: request.headers
              .set("Ocp-Apim-Subscription-Key", this.apimSubscriptionKey)
              .set("Ocp-Apim-Trace", this.apimTrace)
              .set("Authorization", "Bearer " + token)
              .set("x-ui-local-offset", new Date().getTimezoneOffset().toString())
          });
          return next.handle(clonedRequest);
        } else {
          // If the token is not available, return the original request
          return next.handle(request);
        }
      })
    );
  }
}
